/**
 * Tenant Form Schema
 * Zod validation schema for tenant form with react-hook-form integration
 */

import { z } from '@spcc/core'
import type { Tenant, TenantChannel } from '../types/tenant.types'

// ===========================================
// Constants
// ===========================================

/**
 * Field name constants to prevent typos
 */
export const TENANT_FORM_FIELDS = {
  CODE: 'code',
  NAME: 'name',
  LEGAL_NAME: 'legalName',
  TAX_ID: 'taxId',
  VAT_NUMBER: 'vatNumber',
  REGISTRATION_NUMBER: 'registrationNumber',
  CHANNEL: 'channel',
  CUSTOMER_GROUP_ID: 'customerGroupId',
} as const

/**
 * Translation key constants
 */
export const TENANT_FORM_T_KEYS = {
  LABELS: {
    CODE: 'admin.tenant.form.code',
    NAME: 'admin.tenant.form.name',
    LEGAL_NAME: 'admin.tenant.form.legalName',
    TAX_ID: 'admin.tenant.form.taxId',
    VAT_NUMBER: 'admin.tenant.form.vatNumber',
    REGISTRATION_NUMBER: 'admin.tenant.form.registrationNumber',
    CHANNEL: 'admin.tenant.form.channel',
    CUSTOMER_GROUP: 'admin.tenant.form.customerGroup',
  },
  PLACEHOLDERS: {
    CODE: 'admin.tenant.form.codePlaceholder',
    NAME: 'admin.tenant.form.namePlaceholder',
    LEGAL_NAME: 'admin.tenant.form.legalNamePlaceholder',
    TAX_ID: 'admin.tenant.form.taxIdPlaceholder',
    VAT_NUMBER: 'admin.tenant.form.vatNumberPlaceholder',
    REGISTRATION_NUMBER: 'admin.tenant.form.registrationNumberPlaceholder',
  },
  VALIDATION: {
    CODE_REQUIRED: 'admin.tenant.form.validation.codeRequired',
    CODE_MAX_LENGTH: 'admin.tenant.form.validation.codeMaxLength',
    NAME_REQUIRED: 'admin.tenant.form.validation.nameRequired',
    NAME_MAX_LENGTH: 'admin.tenant.form.validation.nameMaxLength',
    CHANNEL_REQUIRED: 'admin.tenant.form.validation.channelRequired',
  },
} as const

// Channel options for the form
const channelOptions = ['b2b', 'ecommerce'] as const

// ===========================================
// Zod Schema
// ===========================================

/**
 * Zod schema for tenant form validation
 */
export const tenantFormSchema = z.object({
  code: z
    .string()
    .min(1, TENANT_FORM_T_KEYS.VALIDATION.CODE_REQUIRED)
    .max(50, TENANT_FORM_T_KEYS.VALIDATION.CODE_MAX_LENGTH)
    .regex(/^[A-Za-z0-9-_]+$/, 'admin.tenant.form.validation.codeFormat'),
  name: z
    .string()
    .min(1, TENANT_FORM_T_KEYS.VALIDATION.NAME_REQUIRED)
    .max(255, TENANT_FORM_T_KEYS.VALIDATION.NAME_MAX_LENGTH),
  legalName: z.string().max(255).optional(),
  taxId: z.string().max(50).optional(),
  vatNumber: z.string().max(50).optional(),
  registrationNumber: z.string().max(100).optional(),
  channel: z.enum(channelOptions, {
    message: TENANT_FORM_T_KEYS.VALIDATION.CHANNEL_REQUIRED,
  }),
  customerGroupId: z.string().optional(),
})

/**
 * Type inferred from the schema
 */
export type TenantFormData = z.infer<typeof tenantFormSchema>

// ===========================================
// Default Values
// ===========================================

/**
 * Default values for a new tenant form
 */
export const defaultTenantFormValues: TenantFormData = {
  code: '',
  name: '',
  legalName: '',
  taxId: '',
  vatNumber: '',
  registrationNumber: '',
  channel: 'b2b',
  customerGroupId: '',
}

// ===========================================
// Transform Functions
// ===========================================

/**
 * Get default form values, optionally from existing tenant (for edit mode)
 */
export function getDefaultValues(tenant?: Tenant | null): TenantFormData {
  if (!tenant) {
    return defaultTenantFormValues
  }

  return tenantToFormData(tenant)
}

/**
 * Transform Tenant entity to form data for editing
 */
export function tenantToFormData(tenant: Tenant): TenantFormData {
  return {
    code: tenant.code,
    name: tenant.name,
    legalName: tenant.legalName ?? '',
    taxId: tenant.taxId ?? '',
    vatNumber: tenant.vatNumber ?? '',
    registrationNumber: tenant.registrationNumber ?? '',
    channel: tenant.channel,
    customerGroupId: tenant.customerGroup?.id ?? '',
  }
}

/**
 * Transform form data to create request format
 */
export function formDataToCreateRequest(data: TenantFormData) {
  return {
    code: data.code.trim().toUpperCase(),
    name: data.name.trim(),
    legalName: data.legalName?.trim() || undefined,
    taxId: data.taxId?.trim() || undefined,
    vatNumber: data.vatNumber?.trim() || undefined,
    registrationNumber: data.registrationNumber?.trim() || undefined,
    channel: data.channel as TenantChannel,
    customerGroupId: data.customerGroupId || undefined,
  }
}

/**
 * Transform form data to update request format
 */
export function formDataToUpdateRequest(data: TenantFormData) {
  return {
    code: data.code.trim().toUpperCase(),
    name: data.name.trim(),
    legalName: data.legalName?.trim() || undefined,
    taxId: data.taxId?.trim() || undefined,
    vatNumber: data.vatNumber?.trim() || undefined,
    registrationNumber: data.registrationNumber?.trim() || undefined,
    channel: data.channel as TenantChannel,
    customerGroupId: data.customerGroupId || undefined,
  }
}

/**
 * Check if the form is in edit mode (has existing tenant)
 */
export function isEditMode(tenant?: Tenant | null): boolean {
  return !!tenant?.id
}
